import { useAppDispatch, useAppSelector } from "@/app/reduxHooks";
import { useDqStatsBySourceFileQuery } from "@/features/adp/queries/useSourceQueries";
import { closeOverlayAsync } from "@/features/adp/state/overlay.thunks";
import type { DataProfilingOverlayData } from "@/features/adp/state/overlaySlice";
import Pagination from "@/shared/components/Pagination/Pagination";
import { Badge } from "@/ui/badge";
import { Button } from "@/ui/button";
import {
    Dialog,
    DialogBody,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/ui/dialog";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/ui/table";
import type { DqStatRecord } from "@/features/adp/services/source/source.types";
import { ArrowLeftIcon, ArrowUpDown, ChevronRight, RefreshCwIcon } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import FieldProfileDetail from "./FieldProfileDetail";
import {
    buildFieldStatsView,
    buildProfilingRows,
    formatNumber,
    type ProfilingRow,
} from "./dataProfiling.utils";

// Backend returns placeholder rows the moment POST /dq-stats is accepted, with
// every stat field null and message_status null. Once the Spark profiling job
// finishes, the same rows are updated with values and message_status="success".
// Treat data as "real" only when at least one record has been marked complete.
function isStatComplete(rec: DqStatRecord): boolean {
    const status = (rec.message_status ?? "").toString().toLowerCase();
    return status === "success" || status === "completed" || status === "complete";
}
function hasCompletedStats(data: DqStatRecord[] | undefined): boolean {
    return Array.isArray(data) && data.some(isStatComplete);
}

type SortKey = "field_name" | "field_data_type";
type SortDirection = "asc" | "desc";

const DEFAULT_PAGE_SIZE = 7;
const PAGE_SIZE_OPTIONS = [7, 10, 25, 50];

function comparator(a: ProfilingRow, b: ProfilingRow, key: SortKey, direction: SortDirection): number {
    const av = (a[key] ?? "").toString().toLowerCase();
    const bv = (b[key] ?? "").toString().toLowerCase();
    if (av === bv) return 0;
    const result = av < bv ? -1 : 1;
    return direction === "asc" ? result : -result;
}

interface BreadcrumbProps {
    view: "list" | "detail";
    onBackToList: () => void;
}

function Breadcrumb({ view, onBackToList }: BreadcrumbProps) {
    return (
        <nav className="flex items-center gap-1 text-sm text-muted-foreground">
            <span>Source Details</span>
            <ChevronRight className="size-3" />
            <span>Explorer</span>
            <ChevronRight className="size-3" />
            {view === "list" ? (
                <span className="text-foreground font-medium">Data Profiling</span>
            ) : (
                <>
                    <button
                        type="button"
                        className="hover:underline"
                        onClick={onBackToList}
                    >
                        Data Profiling
                    </button>
                </>
            )}
        </nav>
    );
}

const DataProfilingDialog = () => {
    const dispatch = useAppDispatch();
    const { isOpen, type, data } = useAppSelector((state) => state.overlay);
    const isVisible = isOpen && type === "data-profiling";
    const overlayData = isVisible ? (data as DataProfilingOverlayData) : null;

    // Pure WS-driven gating. Profiling completion arrives on the `dq_stats`
    // channel with `id === source_file_id` and `status === "success"`. We
    // snapshot the current message on open as a baseline so a stale "success"
    // from a prior run for the same source_file_id is ignored, then fire ONE
    // GET when a new completion message lands. No polling — manual Refresh is
    // the fallback if the socket is dead.
    const dqStatsMessage = useAppSelector((state) =>
        overlayData ? state.jobMonitoring.dqStatsMap[String(overlayData.sourceFileId)] : undefined,
    );

    const baselineMessageRef = useRef<typeof dqStatsMessage>(undefined);

    useEffect(() => {
        baselineMessageRef.current = isVisible ? dqStatsMessage : undefined;
        // Re-baseline only when the dialog opens or the session changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isVisible, overlayData?.sessionId]);

    const isFreshSuccess = useMemo(() => {
        if (!dqStatsMessage) return false;
        if (dqStatsMessage === baselineMessageRef.current) return false;
        const status = (dqStatsMessage.status ?? "").toString().toLowerCase();
        return status === "success" || status === "completed" || status === "complete";
    }, [dqStatsMessage]);

    const statsQuery = useDqStatsBySourceFileQuery(
        overlayData?.sourceFileId,
        overlayData?.sessionId,
        {
            // Only fire the GET after the WS lands a fresh dq_stats success for our
            // source_file_id. Manual Refresh button below calls statsQuery.refetch()
            // directly (which ignores `enabled`), so it works even when WS is dead.
            enabled: Boolean(isVisible && overlayData && isFreshSuccess),
        },
    );

    const hasResults = hasCompletedStats(statsQuery.data);

    const [view, setView] = useState<"list" | "detail">("list");
    const [selectedFieldId, setSelectedFieldId] = useState<number | null>(null);
    const [sortKey, setSortKey] = useState<SortKey>("field_name");
    const [sortDir, setSortDir] = useState<SortDirection>("asc");
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

    useEffect(() => {
        if (!isVisible) {
            setView("list");
            setSelectedFieldId(null);
            setPage(1);
            setSortKey("field_name");
            setSortDir("asc");
        }
    }, [isVisible]);

    const rows = useMemo<ProfilingRow[]>(() => {
        if (!overlayData) return [];
        return buildProfilingRows(overlayData.fields, statsQuery.data);
    }, [overlayData, statsQuery.data]);

    const sortedRows = useMemo(() => {
        const copy = [...rows];
        copy.sort((a, b) => comparator(a, b, sortKey, sortDir));
        return copy;
    }, [rows, sortKey, sortDir]);

    const totalPages = Math.max(1, Math.ceil(sortedRows.length / pageSize));
    const safePage = Math.min(page, totalPages);
    const pagedRows = useMemo(
        () => sortedRows.slice((safePage - 1) * pageSize, safePage * pageSize),
        [sortedRows, safePage, pageSize],
    );

    const selectedRow = useMemo(
        () => rows.find((row) => row.source_file_field_id === selectedFieldId) ?? null,
        [rows, selectedFieldId],
    );

    const detailView = useMemo(
        () => (selectedRow ? buildFieldStatsView(selectedRow) : null),
        [selectedRow],
    );

    const handleOpenChange = (open: boolean) => {
        if (!open) {
            dispatch(closeOverlayAsync());
        }
    };

    const handleToggleSort = (key: SortKey) => {
        if (key === sortKey) {
            setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
            return;
        }
        setSortKey(key);
        setSortDir("asc");
    };

    const handleExpand = (row: ProfilingRow) => {
        setSelectedFieldId(row.source_file_field_id);
        setView("detail");
    };

    const handleBackToList = () => {
        setView("list");
        setSelectedFieldId(null);
    };

    const handleSave = () => {
        toast.success("Source configuration completed successfully");
        dispatch(closeOverlayAsync());
    };

    const renderListBody = () => {
        if (statsQuery.isError) {
            return (
                <div className="flex flex-col items-center gap-3 py-12">
                    <p className="text-sm text-destructive">
                        {statsQuery.error instanceof Error
                            ? statsQuery.error.message
                            : "Failed to load profiling results."}
                    </p>
                    <Button variant="outline" size="sm" onClick={() => statsQuery.refetch()}>
                        <RefreshCwIcon className="size-3.5 mr-1" /> Retry
                    </Button>
                </div>
            );
        }

        if (!hasResults) {
            const isFetching = statsQuery.isFetching;
            return (
                <div className="flex flex-col items-center justify-center gap-3 py-16">
                    <span className="size-10 rounded-full border-2 border-muted border-t-primary animate-spin" />
                    <p className="text-sm text-muted-foreground">
                        {isFetching ? "Loading profiling results…" : "Profiling in progress…"}
                    </p>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => statsQuery.refetch()}
                        disabled={isFetching}
                    >
                        <RefreshCwIcon
                            className={`size-3.5 mr-1 ${isFetching ? "animate-spin" : ""}`}
                        />
                        Refresh
                    </Button>
                </div>
            );
        }

        return (
            <>
                <div className="rounded-md border overflow-auto">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="w-12">#</TableHead>
                                <TableHead>
                                    <SortHeader
                                        active={sortKey === "field_name"}
                                        direction={sortDir}
                                        onClick={() => handleToggleSort("field_name")}
                                    >
                                        Field Name
                                    </SortHeader>
                                </TableHead>
                                <TableHead>
                                    <SortHeader
                                        active={sortKey === "field_data_type"}
                                        direction={sortDir}
                                        onClick={() => handleToggleSort("field_data_type")}
                                    >
                                        Data Type
                                    </SortHeader>
                                </TableHead>
                                <TableHead>Distinct Values</TableHead>
                                <TableHead>Non Distinct Values</TableHead>
                                <TableHead>Null Values</TableHead>
                                <TableHead className="w-20 text-right">Expand</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {pagedRows.map((row, index) => (
                                <TableRow key={row.source_file_field_id}>
                                    <TableCell className="text-muted-foreground">
                                        {(safePage - 1) * pageSize + index + 1}
                                    </TableCell>
                                    <TableCell className="font-medium">{row.field_name}</TableCell>
                                    <TableCell>
                                        <Badge variant="secondary" className="capitalize">
                                            {row.field_data_type}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="tabular-nums">
                                        {formatNumber(row.distinctCount, 0)}
                                    </TableCell>
                                    <TableCell className="tabular-nums">
                                        {formatNumber(row.duplicate, 0)}
                                    </TableCell>
                                    <TableCell className="tabular-nums">
                                        {formatNumber(row.nullValue, 0)}
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => handleExpand(row)}
                                            aria-label={`Expand ${row.field_name}`}
                                        >
                                            <ChevronRight className="size-4" />
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {pagedRows.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                                        No fields available.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </div>

                <Pagination
                    currentPage={safePage}
                    totalPages={totalPages}
                    rowsPerPage={pageSize}
                    onPageChange={setPage}
                    onRowsPerPageChange={(value) => {
                        setPageSize(value);
                        setPage(1);
                    }}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                />
            </>
        );
    };

    return (
        <Dialog open={isVisible} onOpenChange={handleOpenChange}>
            <DialogContent className="max-w-4xl h-[90vh] max-h-[90vh] flex flex-col">
                <DialogHeader className="!flex !flex-row items-center gap-3 justify-start shrink-0">
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={view === "detail" ? handleBackToList : () => handleOpenChange(false)}
                        aria-label="Back"
                    >
                        <ArrowLeftIcon className="size-4" />
                    </Button>
                    <div className="flex flex-col gap-1">
                        <DialogTitle className="text-xl font-semibold tracking-tight">
                            Data Profiling
                        </DialogTitle>
                        <Breadcrumb view={view} onBackToList={handleBackToList} />
                    </div>
                </DialogHeader>

                <DialogBody className="flex-1 min-h-0 flex flex-col gap-4 overflow-auto">
                    {view === "list" && renderListBody()}
                    {view === "detail" && detailView && <FieldProfileDetail view={detailView} />}
                </DialogBody>

                <DialogFooter className="flex items-center justify-end gap-2 shrink-0">
                    <Button variant="outline" onClick={() => handleOpenChange(false)}>
                        Cancel
                    </Button>
                    <Button onClick={handleSave} disabled={!hasResults}>
                        Save
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

interface SortHeaderProps {
    children: React.ReactNode;
    active: boolean;
    direction: SortDirection;
    onClick: () => void;
}

function SortHeader({ children, active, direction, onClick }: SortHeaderProps) {
    return (
        <button
            type="button"
            className="inline-flex items-center gap-1 hover:text-foreground"
            onClick={onClick}
        >
            {children}
            <ArrowUpDown
                className={`size-3 ${active ? "text-foreground" : "text-muted-foreground"} ${
                    active && direction === "desc" ? "rotate-180" : ""
                }`}
            />
        </button>
    );
}

export default DataProfilingDialog;
